import { Formik, Form, useFormikContext, useField } from "formik";
import { Box, Text, Button, Link } from "@/ui";
import { Card } from "../../../ui/Card";
import { TextField } from "../../../ui/field/TextField";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { fetchForgotPassword, fetchResetPassword } from "@/api/auth/fetchAuth";
import { useSystemContext } from "@/provider/common/SystemProvider";
import { Checkbox, FormControlLabel, IconButton, InputAdornment, TextField as MuiTextField, useTheme } from "@mui/material";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function passwordError(value: string) {
  if (!value) return "Le nouveau mot de passe est requis";
  if (value.startsWith(" ")) return "Votre mot de passe ne peut pas commencer par un espace";
  if (value.endsWith(" ")) return "Votre mot de passe ne peut pas se terminer par un espace";
  if (value.length < 6) return "Votre mot de passe doit faire 6 caractères minimum";
  return undefined;
}

// Codes renvoyés par l'API (voir cofidoc-baas/src/lambdas-v2/[api-cognito]/services/{passwordForgotten,changePassword}.ts)
const API_ERROR_MESSAGES: Record<string, string> = {
  PASSWORD_RESET_REQUEST_FAILED: "Impossible d'envoyer l'email de réinitialisation. Réessayez dans quelques instants.",
  ACCOUNT_NOT_CONFIRMED: "Ce compte n'est pas encore confirmé.",
  PASSWORD_RESET_CODE_INVALID: "Le code saisi est invalide.",
  PASSWORD_RESET_CODE_EXPIRED: "Le code a expiré, merci de recommencer la procédure.",
  PASSWORD_INVALID: "Le mot de passe ne respecte pas les critères requis (6 caractères minimum, sans espace au début ou à la fin).",
  PASSWORD_RESET_FAILED: "La réinitialisation du mot de passe a échoué.",
};

// Erreurs qui concernent le code : renvoient l'utilisateur à l'étape du code.
const CODE_ERROR_MESSAGES = new Set(["PASSWORD_RESET_CODE_INVALID", "PASSWORD_RESET_CODE_EXPIRED"]);

function apiErrorMessage(e: any) {
  return API_ERROR_MESSAGES[e?.message] || e?.message || "Une erreur est survenue";
}

function validate(step: number, codeRequested: boolean) {
  return (values: any) => {
    const errors: { email?: string; code?: string; newPassword?: string; confirmPassword?: string; agreed?: string } = {};
    if (step === 1) {
      if (!codeRequested) {
        if (!values.email) {
          errors.email = "L'email est requis";
        } else if (!EMAIL_REGEX.test(values.email)) {
          errors.email = "Merci de saisir une adresse email valide";
        }
      } else {
        if (!values.code) {
          errors.code = "Le code est requis";
        }
      }
    } else {
      const newPasswordError = passwordError(values.newPassword);
      if (newPasswordError) {
        errors.newPassword = newPasswordError;
      }
      if (!values.confirmPassword) {
        errors.confirmPassword = "La confirmation du mot de passe est requise";
      } else if (values.confirmPassword !== values.newPassword) {
        errors.confirmPassword = "Les mots de passe ne correspondent pas";
      }
      if (!values.agreed) {
        errors.agreed = "Vous devez cocher la case pour valider";
      }
    }
    return errors;
  };
}

function SubmitButton({ label }: { label: string }) {
  const { isValid, dirty } = useFormikContext();
  return (
    <Button type="submit" disabled={!dirty || !isValid}>
      {label}
    </Button>
  );
}

function FieldError({ name }: { name: string }) {
  const { errors, touched } = useFormikContext<Record<string, string>>();
  if (!touched[name] || !errors[name]) return null;
  return (
    <Box fontSize={11} color="#ff0000" pt={1}>
      {errors[name]}
    </Box>
  );
}

function ApiError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <Box fontSize={12.5} color="#ff0000" pt={1}>
      {message}
    </Box>
  );
}

function AgreedCheckbox() {
  const [field] = useField({ name: "agreed", type: "checkbox" });
  return (
    <FormControlLabel
      control={<Checkbox {...field} checked={!!field.value} />}
      label={
        <Text fontSize={12} style={{ color: "#5f5f5f" }}>
          J'ai compris que ce nouveau mot de passe sera aussi celui de mon application mobile (Cofidoc Taxi ou Mobility).
        </Text>
      }
    />
  );
}

function PasswordField({ name, label }: { name: string; label: string }) {
  const [field] = useField(name);
  const [visible, setVisible] = useState(false);
  return (
    <MuiTextField
      {...field}
      type={visible ? "text" : "password"}
      label={label}
      fullWidth
      autoComplete="new-password"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => setVisible((v) => !v)} edge="end" size="small" tabIndex={-1}>
              {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

export default function ForgotPasswordView() {
  const [step, setStep] = useState(1);
  const [codeRequested, setCodeRequested] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const { setLoading, isMobile } = useSystemContext();

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timeout = setTimeout(() => setResendCooldown((value) => Math.max(value - 1, 0)), 1000);
    return () => clearTimeout(timeout);
  }, [resendCooldown]);

  async function handleResendCode() {
    if (!email || resendCooldown > 0 || resendLoading) return;
    setResendMessage(null);
    setError(null);
    setResendLoading(true);
    try {
      await fetchForgotPassword({ email });
      setResendMessage("Un nouveau code vous a été envoyé par email.");
      setResendCooldown(60);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setResendLoading(false);
    }
  }

  const submitLabel = step === 1 ? (codeRequested ? "Valider" : "Envoyer") : "Valider";
  const prompt =
    step === 1
      ? codeRequested
        ? "Entrez le code reçu par email"
        : "Saisissez l'adresse email de votre compte de l'application mobile (Cofidoc Taxi ou Mobility)"
      : "Choisissez votre nouveau mot de passe";

  return (
    <Box height="100vh" justifyContent="center" alignItems="center" width="100vw" style={{ backgroundColor: "#f1f1f1" }}>
      <div className="background-cloud height40" />
      <div className="background-over" />
      <div className="background-mountain-cofidoc" />
      <Box display="flex" p={2} alignItems="center" justifyContent="center" zIndex={2} position="absolute" top={20} left={20}>
        <div className="background-logo" />
      </Box>
      <Box
        width="100%"
        style={{
          position: "relative",
          zIndex: 4,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Card justifyContent="center" alignItems="center" display="flex" flexDirection="column" width={isMobile ? "90%" : "650px"}>
          <Formik
            key={step === 1 ? `1-${codeRequested}` : "2"}
            initialValues={step === 1 ? { email, code } : { newPassword: "", confirmPassword: "", agreed: false }}
            validate={validate(step, codeRequested)}
            onSubmit={async (values: any) => {
              try {
                setLoading(true);
                setError(null);
                if (step === 1) {
                  if (!codeRequested) {
                    await fetchForgotPassword({ email: values.email });
                    setEmail(values.email);
                    setCodeRequested(true);
                    setResendCooldown(60);
                  } else {
                    setCode(values.code);
                    setStep(2);
                  }
                } else {
                  await fetchResetPassword({
                    email,
                    code,
                    password: values.newPassword,
                  });
                  navigate("/login", { state: { successMessage: "Mot de passe modifié avec succès." } });
                }
              } catch (e: any) {
                console.log("error", e);
                if (step === 2 && CODE_ERROR_MESSAGES.has(e?.message)) {
                  setError(apiErrorMessage(e));
                  setResendMessage(null);
                  setStep(1);
                  setCodeRequested(true);
                } else {
                  setError(apiErrorMessage(e));
                }
              } finally {
                setLoading(false);
              }
            }}
          >
            <Form
              style={{
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box display="flex" width="100%" p={2}>
                <Link onClick={() => navigate("/login")}>
                  <ArrowBackIcon fontSize="small" />
                  <Box pr={1} />
                  <Text
                    style={{
                      textAlign: "center",
                      fontSize: 12,
                      fontStyle: "italic",
                    }}
                  >
                    Retour au portail de connexion
                  </Text>
                </Link>
              </Box>

              <Box style={{ margin: 20 }}>
                <Text fontSize={20} fontWeight={500} textAlign="center" color={theme.colors.primary}>
                  Première connexion / J'ai oublié mon mot de passe
                </Text>
              </Box>
              <Box style={{ margin: 20, textAlign: "center" }}>
                <Text style={{ textAlign: "center" }}>{prompt}</Text>
              </Box>
              <Box width="80%">
                {step === 1 ? (
                  !codeRequested ? (
                    <Box width={"100%"} display={"flex"} flexDirection={"column"}>
                      <TextField label="Email" name="email" type="email" width="100%" />
                      <FieldError name="email" />
                      <ApiError message={error} />
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{
                          backgroundColor: `${theme.colors.red.pale}40`,
                          borderLeft: `3px solid ${theme.colors.red.main}`,
                          borderRadius: 6,
                          padding: "10px 14px",
                          marginTop: 16,
                        }}
                      >
                        <WarningAmberIcon style={{ color: theme.colors.red.dark, fontSize: 20, flexShrink: 0 }} />
                        <Text fontSize={12.5} style={{ color: theme.colors.red.dark, lineHeight: 1.4, paddingLeft: 10 }}>
                          <b>Attention</b> : modifier votre mot de passe le changera aussi sur votre application mobile (Cofidoc Taxi ou Mobility).
                        </Text>
                      </Box>
                    </Box>
                  ) : (
                    <Box width={"100%"} display={"flex"} flexDirection={"column"}>
                      <TextField label="Email" name="email" type="email" width="100%" disabled />
                      <Box height={16} />
                      <TextField label="Code" name="code" width="100%" autoComplete="one-time-code" />
                      <FieldError name="code" />
                      <ApiError message={error} />
                      <Box mt={1}>
                        <Text fontSize={12.5} style={{ color: theme.colors.grey.main }}>
                          Si vous ne trouvez pas l'email, pensez à vérifier vos spams.
                        </Text>
                      </Box>
                      {resendMessage && (
                        <Box mt={1}>
                          <Text fontSize={12.5} style={{ color: theme.colors.green.dark }}>
                            {resendMessage}
                          </Text>
                        </Box>
                      )}
                      <Box mt={2}>
                        <Button
                          type="button"
                          color="grey"
                          onClick={handleResendCode}
                          disabled={resendCooldown > 0 || resendLoading}
                        >
                          {resendLoading
                            ? "Envoi en cours..."
                            : resendCooldown > 0
                              ? `Renvoyer le code (${resendCooldown}s)`
                              : "Renvoyer le code"}
                        </Button>
                      </Box>
                    </Box>
                  )
                ) : (
                  <Box width={"100%"} display={"flex"} flexDirection={"column"}>
                    <PasswordField name="newPassword" label="Nouveau mot de passe" />
                    <FieldError name="newPassword" />
                    <Box height={16} />
                    <PasswordField name="confirmPassword" label="Confirmer le mot de passe" />
                    <FieldError name="confirmPassword" />
                    <Box height={8} />
                    <AgreedCheckbox />
                    <ApiError message={error} />
                  </Box>
                )}
              </Box>
              <Box flex={1} justifyContent="center" mt={3}>
                <SubmitButton label={submitLabel} />
              </Box>
              <Box justifyContent="center" alignItems="center" height={60}>
                <Link onClick={() => navigate("/login")}>
                  <Text style={{ textAlign: "center", fontSize: 12 }}>Annuler</Text>
                </Link>
              </Box>
            </Form>
          </Formik>
        </Card>
      </Box>
      <Box style={{ position: "absolute", bottom: 0, zIndex: 3, padding: 5 }}>
        <Text style={{ textAlign: "center", fontSize: 12 }}>© 2024 Cofidoc Groupe</Text>
      </Box>
    </Box>
  );
}
